import ColumnFilter from './ColumnFilter'
import type { ColumnSelection } from './columnFilters'
import { STAFFING_STATUS_HEADER, staffingStatusLabel, type StaffingFilters, type StaffingSummary } from './staffingSummary'

const HEADER = 'px-4 py-2.5 text-caption font-bold tracking-wide text-ink-subtle uppercase'
const CELL = 'px-4 py-2.5 text-small [font-variant-numeric:tabular-nums]'

/**
 * The staffing figures above a report that lists people: required, available,
 * and how far short or over, with a total. The numbers come from
 * staffingSummary.ts; the same result is written into the exports.
 */
export default function StaffingSummaryTable({
  heading,
  note,
  summary,
  shown,
  filters,
  onFiltersChange,
  loading,
  failed,
  empty,
}: {
  heading: string
  note: string
  // Every row, which the column filters list their values from.
  summary: StaffingSummary
  // The rows the filters leave, with their total.
  shown: StaffingSummary
  filters: StaffingFilters
  onFiltersChange: (filters: StaffingFilters) => void
  loading: boolean
  failed: boolean
  empty: string
}) {
  const filtered = Object.values(filters).some((selected) => selected !== undefined)
  const filterFor = (header: string, options: { value: string; label: string }[]) => ({
    options,
    selected: filters[header],
    onChange: (selected: ColumnSelection) => onFiltersChange({ ...filters, [header]: selected }),
  })
  const valuesAt = (index: number) =>
    Array.from(new Set(summary.rows.map((row) => row.labels[index])))
      .sort()
      .map((value) => ({ value, label: value }))
  const statuses = Array.from(new Set(summary.rows.map((row) => row.status))).map((status) => ({ value: status, label: staffingStatusLabel(status) }))

  return (
    <section aria-label={heading} className="rounded-card border border-line bg-canvas">
      <div className="border-b border-line px-4 py-3">
        <h2 className="text-body font-extrabold">{heading}</h2>
        <p className="text-caption text-ink-muted">{note}</p>
        {filtered && (
          <button type="button" onClick={() => onFiltersChange({})} className="min-h-11 rounded-control text-small font-bold text-primary hover:bg-surface-2">
            Clear filters
          </button>
        )}
      </div>
      {failed ? (
        <p role="alert" className="p-4 text-small text-error">
          The staffing figures could not be loaded. Reload the page, or try again in a moment.
        </p>
      ) : loading ? (
        <div aria-hidden="true" className="flex flex-col gap-2 p-4">
          {[0, 1].map((line) => (
            <div key={line} className="h-8 animate-pulse rounded-control bg-surface-2" />
          ))}
        </div>
      ) : summary.rows.length === 0 ? (
        <p className="p-4 text-small text-ink-muted">{empty}</p>
      ) : (
        <div role="region" aria-label={`${heading} table`} tabIndex={0} className="overflow-x-auto">
          <table className="w-full border-collapse" style={{ minWidth: 640 }}>
            <caption className="sr-only">{heading}</caption>
            <thead>
              <tr className="border-b border-line bg-surface-2 text-left">
                {summary.labelHeaders.map((header, index) => (
                  <th key={header} scope="col" className={HEADER}>
                    {header}
                    <ColumnFilter label={header} {...filterFor(header, valuesAt(index))} />
                  </th>
                ))}
                {['Required', 'Available', 'Short by', 'Over by'].map((header) => (
                  <th key={header} scope="col" className={`${HEADER} text-right`}>
                    {header}
                  </th>
                ))}
                <th scope="col" className={HEADER}>
                  Status
                  <ColumnFilter label={STAFFING_STATUS_HEADER} {...filterFor(STAFFING_STATUS_HEADER, statuses)} />
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.rows.length === 0 && (
                <tr>
                  <td colSpan={summary.labelHeaders.length + 5} className="px-4 py-4 text-small text-ink-muted">
                    No rows match the filters.
                  </td>
                </tr>
              )}
              {shown.rows.map((row) => (
                <tr key={row.key} className="border-b border-line">
                  {row.labels.map((label, index) =>
                    index === 0 ? (
                      <th key={index} scope="row" className={`${CELL} text-left font-semibold`}>
                        {label}
                      </th>
                    ) : (
                      <td key={index} className={CELL}>
                        {label}
                      </td>
                    ),
                  )}
                  <td className={`${CELL} text-right`}>{row.required}</td>
                  <td className={`${CELL} text-right`}>{row.available}</td>
                  <td className={`${CELL} text-right ${row.short > 0 ? 'font-bold text-error' : ''}`}>{row.short}</td>
                  <td className={`${CELL} text-right`}>{row.over}</td>
                  <td className={CELL}>{staffingStatusLabel(row.status)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-surface-2 font-bold">
                <th scope="row" colSpan={summary.labelHeaders.length} className={`${CELL} text-left`}>
                  Total
                </th>
                <td className={`${CELL} text-right`}>{shown.total.required}</td>
                <td className={`${CELL} text-right`}>{shown.total.available}</td>
                <td className={`${CELL} text-right ${shown.total.short > 0 ? 'text-error' : ''}`}>{shown.total.short}</td>
                <td className={`${CELL} text-right`}>{shown.total.over}</td>
                <td className={CELL}>{staffingStatusLabel(shown.total.status)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  )
}
